import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../lib/api";
import Breadcrumbs from "../components/Breadcrumbs";
import Disclaimer from "../components/Disclaimer";
import { CallCard } from "../components/PhoneCTA";
import MedicalImage from "../components/MedicalImage";
import { Search, MapPin, GraduationCap } from "lucide-react";
import { ScheduleConsultationCTA } from "../components/ScheduleConsultationCTA";
import { robotsMeta } from "../lib/seo_env";

const ORIGIN = "https://medicalpeptides.ai";

// Read the Puppeteer-injected __PRERENDER_CACHE__ synchronously for the first
// render pass. Same helper as CmsPage — kept local to avoid a shared-utility
// churn during Stage 3. If no snapshot injected a cache, returns null and the
// component falls back to the normal client-side fetch.
function readPrerenderCache(key) {
  if (typeof window === "undefined") return null;
  const cache = window.__PRERENDER_CACHE__;
  if (!cache) return null;
  return cache[key] ?? cache[`/api${key}`] ?? null;
}

/**
 * Providers directory. Two modes:
 *   1. `/providers` — full listing with search + filters
 *   2. `/providers/{state}` — providers licensed in a given state
 *
 * The routing decision (state page vs individual provider profile at the same
 * URL depth) is made by `ProvidersRoute` in App.js. Existing individual
 * provider URLs (e.g. /providers/yessenia-quirola) are preserved untouched;
 * only state-slug URLs (nevada, florida, etc.) are captured here.
 *
 * All data comes from the CMS — as new provider rows are added and set
 * `public_visibility=true`, they appear here automatically. Retired providers
 * (status !== published) automatically drop out.
 */
export default function ProvidersDirectory({ stateFilter = null }) {
  const [providers, setProviders] = useState(() => readPrerenderCache("/content/provider") || []);
  const [stateDoc, setStateDoc] = useState(() =>
    stateFilter ? readPrerenderCache(`/content/state/${stateFilter}`) : null
  );
  const [q, setQ] = useState("");
  const [stateSel, setStateSel] = useState(stateFilter || "");
  const [specialtySel, setSpecialtySel] = useState("");

  useEffect(() => {
    if (providers.length) return; // seeded from prerender cache
    api.get("/content/provider")
      .then(({ data }) => setProviders(data))
      .catch(() => setProviders([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // When URL is /providers/{stateSlug}, load the state page for context
    // (related articles / cities / peptides for cross-linking).
    if (!stateFilter) { setStateDoc(null); return; }
    if (stateDoc && stateDoc.slug === stateFilter) return;
    api.get(`/content/state/${stateFilter}`)
      .then(({ data }) => setStateDoc(data))
      .catch(() => setStateDoc(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFilter]);

  // Available filter options derived from CMS data
  const allStates = useMemo(() => {
    const s = new Set();
    providers.forEach((p) => (p.extra?.state_licenses || []).forEach((st) => s.add(st)));
    return Array.from(s).sort();
  }, [providers]);

  const allSpecialties = useMemo(() => {
    const s = new Set();
    providers.forEach((p) => {
      if (p.extra?.role_label) s.add(p.extra.role_label);
      if (p.extra?.focus) s.add(p.extra.focus);
    });
    return Array.from(s).sort();
  }, [providers]);

  const filtered = useMemo(() => {
    // Both sides are compared as slugs so "Nevada" and "nevada" match.
    const toSlug = (s) => (s || "").toLowerCase().replace(/\s+/g, "-");
    const wantedState = toSlug(stateSel);
    return providers.filter((p) => {
      if (wantedState) {
        const licenses = (p.extra?.state_licenses || []).map(toSlug);
        if (!licenses.includes(wantedState)) return false;
      }
      if (specialtySel) {
        const bag = [p.extra?.role_label, p.extra?.focus].filter(Boolean).join(" ");
        if (!bag.toLowerCase().includes(specialtySel.toLowerCase())) return false;
      }
      if (q) {
        const hay = [p.title, p.subtitle, p.summary_answer, p.extra?.focus].join(" ").toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [providers, q, stateSel, specialtySel]);

  const isStatePage = Boolean(stateFilter);
  const pageTitle = isStatePage
    ? `${stateNamePretty(stateFilter)} Peptide Therapy Providers | Medical Peptides`
    : "Meet Our Licensed Medical Providers | MedicalPeptides.ai";
  const desc = isStatePage
    ? `Meet the licensed clinicians serving ${stateNamePretty(stateFilter)} via VidaVital Medical telehealth. View credentials, focus areas, and educational content.`
    : "Meet the licensed healthcare professionals behind MedicalPeptides.ai and VidaVital Medical. View provider profiles, credentials, licensed states, specialties, and educational content.";
  const canonical = ORIGIN + (isStatePage ? `/providers/${stateFilter}` : "/providers");

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: pageTitle,
    itemListElement: filtered.map((p, idx) => {
      const x = p.extra || {};
      const person = {
        "@type": "Person",
        "@id": `${ORIGIN}/providers/${p.slug}`,
        name: p.title,
        url: `${ORIGIN}/providers/${p.slug}`,
      };
      if (x.credentials) person.honorificSuffix = x.credentials;
      if (x.role_label || p.subtitle) person.jobTitle = x.role_label || p.subtitle;
      if (Array.isArray(x.specialties) && x.specialties.length) person.knowsAbout = x.specialties;
      if (Array.isArray(x.state_licenses) && x.state_licenses.length) {
        person.areaServed = x.state_licenses.map((s) => ({ "@type": "State", name: s }));
      }
      if (Array.isArray(x.languages) && x.languages.length) person.knowsLanguage = x.languages;
      if (x.npi) {
        person.identifier = { "@type": "PropertyValue", propertyID: "NPI", value: String(x.npi) };
      }
      if (p.featured_image) {
        person.image = p.featured_image.startsWith("http") ? p.featured_image : `${ORIGIN}${p.featured_image}`;
      }
      const affiliation = {
        "@type": "MedicalBusiness",
        name: "VidaVital Medical",
        url: "https://vidavitalmedical.com",
      };
      person.worksFor = affiliation;
      person.affiliation = affiliation;
      return { "@type": "ListItem", position: idx + 1, item: person };
    }),
  };

  const bcSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: "Our Providers", item: `${ORIGIN}/providers` },
      ...(isStatePage
        ? [{ "@type": "ListItem", position: 3, name: stateNamePretty(stateFilter), item: canonical }]
        : []),
    ],
  };

  return (
    <div data-testid={isStatePage ? `providers-state-${stateFilter}` : "providers-directory"}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content={robotsMeta()} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Medical Peptides Powered by VidaVital Medical" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${ORIGIN}/social-preview.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={desc} />
        <meta name="twitter:image" content={`${ORIGIN}/social-preview.jpg`} />
        <script type="application/ld+json">{JSON.stringify(itemList)}</script>
        <script type="application/ld+json">{JSON.stringify(bcSchema)}</script>
      </Helmet>

      <section className="vv-hero">
        <div className="vv-container py-12">
          <Breadcrumbs items={isStatePage
            ? [{ name: "Our Providers", path: "/providers" }, { name: stateNamePretty(stateFilter), path: `/providers/${stateFilter}` }]
            : [{ name: "Our Providers", path: "/providers" }]} />
          <div className="vv-hero-eyebrow mb-2">Meet the team</div>
          <h1 className="font-display text-4xl md:text-5xl">
            {isStatePage ? `${stateNamePretty(stateFilter)} Medical Providers` : "Meet Our Medical Providers"}
          </h1>
          <p className="mt-3 text-stone-600 max-w-3xl">
            {isStatePage
              ? `Every consultation for ${stateNamePretty(stateFilter)} residents is performed by a state-licensed clinician — in person at a participating clinic or via telemedicine where legally appropriate.`
              : "Every consultation on MedicalPeptides.ai is performed by a licensed medical provider — in person at a participating clinic or via telemedicine where legally appropriate. Explore our team, their credentials, and the states each is licensed to serve."}
          </p>
          <div className="mt-6">
            <ScheduleConsultationCTA
              location={isStatePage ? `providers-state-${stateFilter}` : "providers-directory-hero"}
              variant="gold"
              size="lg"
            />
          </div>
        </div>
      </section>

      <section className="vv-container py-8">
        {/* Filters */}
        <div className="vv-card p-4 mb-6 grid md:grid-cols-[1fr_180px_200px] gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="providers-search"
              placeholder="Search by name, focus, or specialty…"
              className="w-full bg-white border border-stone-200 rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:border-[color:var(--vv-teal)]"
            />
          </div>
          {!isStatePage && (
            <select
              data-testid="providers-state-filter"
              value={stateSel}
              onChange={(e) => setStateSel(e.target.value)}
              className="bg-white border border-stone-200 rounded-full px-4 py-2 text-sm"
            >
              <option value="">All states</option>
              {allStates.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          <select
            data-testid="providers-specialty-filter"
            value={specialtySel}
            onChange={(e) => setSpecialtySel(e.target.value)}
            className="bg-white border border-stone-200 rounded-full px-4 py-2 text-sm"
          >
            <option value="">All specialties / services</option>
            {allSpecialties.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* Results */}
        <div className="text-xs text-stone-500 mb-4">
          {`Showing ${filtered.length} of ${providers.length} providers${isStatePage ? ` licensed in ${stateNamePretty(stateFilter)}` : ""}.`}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p) => <ProviderCard key={p.slug} p={p} />)}
        </div>

        {filtered.length === 0 && (
          <div className="vv-card p-8 text-center text-stone-500">
            No providers match those filters.
          </div>
        )}
      </section>

      {/* Internal linking — state context (when on a state page) */}
      {isStatePage && stateDoc && (
        <section className="vv-container py-8 grid md:grid-cols-3 gap-6">
          <RelatedBlock title={`${stateNamePretty(stateFilter)} clinic locations`} items={stateDoc.related_cities} basePath={`/locations/${stateFilter}`} />
          <RelatedBlock title={`Related articles`} items={stateDoc.related_articles} basePath="/articles" />
          <RelatedBlock title={`Related peptides`} items={stateDoc.related_peptides} basePath="/peptides" />
        </section>
      )}

      <section className="vv-container py-10">
        <div className="grid md:grid-cols-[1fr_320px] gap-6">
          <div className="vv-card p-6">
            <div className="text-xs uppercase tracking-widest text-stone-500">How consultations work</div>
            <p className="mt-2 text-stone-700">
              After you request a consultation, an eligible licensed clinician meets you by video call. All treatment
              decisions require a live evaluation and are made only by a licensed medical provider.
            </p>
          </div>
          <CallCard />
        </div>
        <div className="mt-6"><Disclaimer /></div>
      </section>
    </div>
  );
}

function ProviderCard({ p }) {
  const licensed = (p.extra?.state_licenses || []).slice(0, 4);
  const remainder = Math.max(0, (p.extra?.state_licenses || []).length - licensed.length);
  const languages = p.extra?.languages || [];
  const mediaId = p.extra?.photo_media_id;
  const [media, setMedia] = useState(() =>
    mediaId ? readPrerenderCache(`/media/${mediaId}`) : null
  );
  useEffect(() => {
    if (!mediaId) return;
    if (media && media.id === mediaId) return;
    let cancelled = false;
    api.get(`/media/${mediaId}`)
      .then(({ data }) => { if (!cancelled) setMedia(data); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId]);

  const shortBio = (p.summary_answer || "").slice(0, 220) + ((p.summary_answer || "").length > 220 ? "…" : "");

  return (
    <div className="vv-card p-5" data-testid={`provider-card-${p.slug}`}>
      <div className="flex items-start gap-4">
        <div className="w-20 shrink-0">
          {media ? (
            <MedicalImage
              media={media}
              sizes="80px"
              className="rounded-2xl w-20 h-20 object-cover shadow-sm"
              testId={`provider-card-photo-${p.slug}`}
            />
          ) : p.featured_image ? (
            <img src={p.featured_image} alt={p.extra?.photo_alt || p.title}
                 width={80} height={80} loading="lazy"
                 className="rounded-2xl w-20 h-20 object-cover shadow-sm" />
          ) : (
            <div className="rounded-2xl w-20 h-20 bg-stone-100" aria-hidden="true" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg leading-tight">{p.title}</div>
          {p.subtitle ? <div className="text-xs text-stone-500 mt-0.5">{p.subtitle}</div> : null}
          {p.extra?.focus ? (
            <span className="inline-block mt-2 text-[10px] uppercase tracking-widest bg-amber-50 text-amber-900 border border-amber-200 rounded-full px-2 py-0.5">
              {p.extra.focus}
            </span>
          ) : null}
        </div>
      </div>

      {shortBio && <p className="mt-3 text-sm text-stone-700 leading-relaxed">{shortBio}</p>}

      <div className="mt-3 text-xs text-stone-600 space-y-1">
        {licensed.length ? (
          <div className="flex items-start gap-1.5"><MapPin size={13} className="mt-0.5 shrink-0" />
            <span>{`Licensed: ${licensed.join(", ")}${remainder ? ` +${remainder} more` : ""}`}</span>
          </div>
        ) : null}
        {languages.length ? (
          <div className="flex items-start gap-1.5"><GraduationCap size={13} className="mt-0.5 shrink-0" />
            <span>{`Languages: ${languages.join(", ")}`}</span>
          </div>
        ) : null}
      </div>

      <Link
        to={`/providers/${p.slug}`}
        data-testid={`provider-card-link-${p.slug}`}
        className="vv-btn-outline mt-4 inline-flex w-full justify-center text-sm"
      >
        View profile →
      </Link>
    </div>
  );
}

function RelatedBlock({ title, items, basePath }) {
  if (!items?.length) return null;
  return (
    <div className="vv-card p-5">
      <div className="text-xs uppercase tracking-widest text-stone-500">{title}</div>
      <ul className="mt-3 space-y-1">
        {items.slice(0, 8).map((slug) => (
          <li key={slug}>
            <Link to={`${basePath}/${slug}`} className="text-sm text-[color:var(--vv-gold-deep)] hover:underline">
              {slug.replace(/-/g, " ")}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function stateNamePretty(slug) {
  return (slug || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
